import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { billing, fmtMoney } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";

const inputCls = "bg-slate-950 border-slate-700 h-9";
const selCls = "h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm";

const DEST_KEYS = { canada: "canada", portugal: "portugal", "estados unidos": "usa", "united states": "usa", usa: "usa" };

export const ProposalEditor = ({ open, onOpenChange, processId, destination, suggestedEntity, proposal, onSaved }) => {
  const { bt, lang } = useBT();
  const [entities, setEntities] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    billing.entities().then(setEntities).catch(() => {});
    const dest = DEST_KEYS[(destination || "").toLowerCase()] || (destination || "").toLowerCase();
    billing.catalog(dest ? { destination: dest } : {}).then(setCatalog).catch(() => {});
    setForm(proposal ? {
      entity_code: proposal.entity_code, title: proposal.title || "", notes: proposal.notes || "",
      language: proposal.language || "en", valid_days: proposal.valid_days || 15,
      lines: (proposal.lines || []).map((l) => ({ item_id: l.item_id, description: l.description, qty: l.qty, unit_price: l.unit_price, vat_rate: l.vat_rate, category: l.category })),
    } : { entity_code: suggestedEntity || "EU", title: "", notes: "", language: "", valid_days: "", lines: [] });
  }, [open, proposal, destination, suggestedEntity]);

  const entity = entities.find((e) => e.code === form?.entity_code);
  const currency = entity?.currency || (form?.entity_code === "AO" ? "AOA" : "EUR");
  const defVat = Number(entity?.vat_rate || 0);

  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    (form?.lines || []).forEach((l) => {
      const net = Number(l.qty || 0) * Number(l.unit_price || 0);
      sub += net;
      vat += net * Number(l.vat_rate ?? defVat) / 100;
    });
    return { sub, vat, total: sub + vat };
  }, [form, defVat]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));

  const addItem = (id) => {
    const it = catalog.find((c) => c.id === id);
    if (!it) return;
    const price = it.prices?.[currency];
    set("lines", [...form.lines, {
      item_id: it.id, category: it.category, qty: 1, unit_price: price ?? 0,
      description: it.name?.[form.language || lang] || it.name?.en || it.code,
      vat_rate: it.vat_exempt ? 0 : undefined,
    }]);
    if (price === undefined) toast.warning(`${it.code}: ${bt("noPrice")} (${currency})`);
  };

  const save = async () => {
    const lines = form.lines.filter((l) => l.description && Number(l.qty) > 0).map((l) => ({
      ...l, qty: Number(l.qty), unit_price: Number(l.unit_price || 0),
      vat_rate: l.vat_rate === undefined || l.vat_rate === "" || l.vat_rate === null ? null : Number(l.vat_rate),
    }));
    const body = { entity_code: form.entity_code, title: form.title || null, notes: form.notes || null, lines,
                   language: form.language || null, valid_days: form.valid_days ? Number(form.valid_days) : null };
    setSaving(true);
    try {
      const res = proposal ? await billing.updateProposal(proposal.id, body) : await billing.createProposal(processId, body);
      onSaved?.(res);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-slate-900 border-slate-700 text-slate-100 max-h-[92vh] overflow-y-auto" data-testid="proposal-editor">
        <DialogHeader>
          <DialogTitle>{proposal ? bt("editProposal") : bt("newProposal")}</DialogTitle>
          <DialogDescription className="text-slate-400">{bt("needsApproval")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-4">
          <label className="text-xs text-slate-400 sm:col-span-2">{bt("title")}
            <Input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="proposal-title" />
          </label>
          <label className="text-xs text-slate-400">{bt("company")}
            <select className={`${selCls} w-full`} value={form.entity_code} onChange={(e) => set("entity_code", e.target.value)} data-testid="proposal-entity">
              {(entities.length ? entities : [{ code: "AO", currency: "AOA" }, { code: "EU", currency: "EUR" }]).map((e) => (
                <option key={e.code} value={e.code}>{e.code} · {e.legal_name || ""} ({e.currency})</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-400">{bt("language")}
              <select className={`${selCls} w-full`} value={form.language} onChange={(e) => set("language", e.target.value)}>
                <option value="">auto</option><option value="pt">PT</option><option value="en">EN</option><option value="fr">FR</option>
              </select>
            </label>
            <label className="text-xs text-slate-400">{bt("validDays")}
              <Input type="number" min="1" className={inputCls} value={form.valid_days} onChange={(e) => set("valid_days", e.target.value)} />
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800">
          <div className="hidden sm:grid grid-cols-[1fr_70px_130px_70px_120px_36px] gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-800">
            <span>{bt("description")}</span><span>{bt("qty")}</span><span>{bt("unitPrice")} ({currency})</span><span>{bt("vat")}</span><span className="text-right">{bt("total")}</span><span />
          </div>
          {form.lines.map((l, i) => {
            const net = Number(l.qty || 0) * Number(l.unit_price || 0);
            return (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_70px_130px_70px_120px_36px] gap-2 px-3 py-2 border-b border-slate-800/60 items-center" data-testid={`proposal-line-${i}`}>
                <Input className={`${inputCls} col-span-2 sm:col-span-1`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                <Input type="number" min="0" step="1" className={inputCls} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                <Input type="number" min="0" step="0.01" className={inputCls} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                <Input type="number" min="0" step="0.1" placeholder={String(defVat)} className={inputCls} value={l.vat_rate ?? ""} onChange={(e) => setLine(i, "vat_rate", e.target.value)} />
                <span className="text-right text-sm tabular-nums">{fmtMoney(net, "")}</span>
                <button type="button" className="text-slate-500 hover:text-red-300 justify-self-end" onClick={() => set("lines", form.lines.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 p-3">
            <select className={`${selCls} max-w-full sm:max-w-md`} value="" onChange={(e) => addItem(e.target.value)} data-testid="proposal-add-catalog">
              <option value="">+ {bt("addFromCatalogue")}…</option>
              {catalog.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.name?.[lang] || c.name?.en || c.code)} — {c.prices?.[currency] !== undefined ? fmtMoney(c.prices[currency], currency) : bt("noPrice")}
                </option>
              ))}
            </select>
            <Button type="button" size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={() => set("lines", [...form.lines, { description: "", qty: 1, unit_price: 0 }])} data-testid="proposal-add-line">
              <Plus className="h-3.5 w-3.5 mr-1" />{bt("addLine")}
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <label className="text-xs text-slate-400 flex-1">{bt("notes")}
            <Textarea rows={3} className="bg-slate-950 border-slate-700" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </label>
          <div className="min-w-[220px] text-sm space-y-1 self-end">
            <div className="flex justify-between text-slate-400"><span>{bt("subtotal")}</span><span className="tabular-nums">{fmtMoney(totals.sub, currency)}</span></div>
            <div className="flex justify-between text-slate-400"><span>{bt("vat").replace(" %", "")}</span><span className="tabular-nums">{fmtMoney(totals.vat, currency)}</span></div>
            <div className="flex justify-between font-semibold text-base border-t border-slate-700 pt-1"><span>{bt("total")}</span><span className="tabular-nums" data-testid="proposal-total">{fmtMoney(totals.total, currency)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{bt("cancel")}</Button>
          <Button className="bg-venka-green text-slate-950 hover:bg-venka-green-light" disabled={saving || !form.lines.length} onClick={save} data-testid="proposal-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{bt("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalEditor;
